/**
 * Room-list panel (default right-panel)
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license AGPLv3
 */

import { Config, PS, PSRoom, type RoomID, type RoomOptions } from "./client-main";
import { BFFacilities, BFSampleTeams, type BFFacilityInfo } from "./bf-facilities"; // BF-HUB-IMPORT
import { PSIcon, PSPanelWrapper, PSRoomPanel } from "./panels"; // BF-HUB-PSICON
import type { RoomInfo } from "./panel-mainmenu";
import { Dex, toID } from "./battle-dex";

export class RoomsRoom extends PSRoom {
	override readonly classType: string = 'rooms';
	constructor(options: RoomOptions) {
		super(options);
		if (Object.keys(PS.prefs.serversettings).length) {
			PS.send(`/updatesettings ${JSON.stringify(PS.prefs.serversettings)}`);
		}
		PS.send(`/cmd rooms`);
	}
}

type RoomsSection = [string, RoomInfo[]];
class RoomsPanel extends PSRoomPanel {
	static readonly id = 'rooms';
	static readonly routes = ['rooms'];
	static readonly Model = RoomsRoom;
	static readonly location = 'right';
	static readonly icon = <i class="fa fa-plus rooms-plus" aria-hidden></i>;
	static readonly title = "Chat Rooms";
	hidden = false;
	search = '';
	section = '';
	lastKeyCode = 0;
	roomList: RoomsSection[] = [];
	roomListFocusIndex = -1;
	roomListLength = 0;
	override componentDidMount() {
		super.componentDidMount();
		this.subscriptions.push(PS.user.subscribe(update => {
			if (!update && PS.user.named) PS.send(`/cmd rooms`);
		}));
	}
	override componentDidUpdate() {
		super.componentDidUpdate();
		const el = this.base?.querySelector('a.blocklink.cur');
		if (!this.roomListFocusIndex) return;
		el?.scrollIntoView({ behavior: 'auto', block: 'center' });
	}
	hide = (ev: Event) => {
		ev.stopImmediatePropagation();
		PS.hideRightRoom();
	};
	changeSearch = (ev: Event) => {
		const target = ev.currentTarget as HTMLInputElement;
		if (target.selectionStart !== target.selectionEnd) return;
		if (target.value.startsWith('/')) {
			this.updateRoomList(target.value);
			this.forceUpdate();
			return;
		}
		this.updateRoomList(target.value);
		this.forceUpdate();
	};
	changeSection = (ev: Event) => {
		const target = ev.currentTarget as HTMLSelectElement;
		this.section = target.value;
		this.forceUpdate();
	};
	handleOnBlur = (ev: Event) => {
		this.roomListFocusIndex = -1;
		this.forceUpdate();
	};
	keyDownSearch = (ev: KeyboardEvent) => {
		this.lastKeyCode = ev.keyCode;
		if (ev.shiftKey || ev.ctrlKey || ev.altKey || ev.metaKey) return;
		if (ev.keyCode === 38) { // up
			this.roomListFocusIndex = Math.max(this.roomListFocusIndex - 1, this.search ? 0 : -1);
			this.forceUpdate();
			ev.preventDefault();
		} else if (ev.keyCode === 40) { // down
			this.roomListFocusIndex = Math.min(this.roomListFocusIndex + 1, this.roomListLength - 1);
			this.forceUpdate();
			ev.preventDefault();
		} else if (ev.keyCode === 13) { // enter
			const target = ev.currentTarget as HTMLInputElement;
			let value = target.value.startsWith('/') ? target.value : this.getRoomListFocusTitle() || target.value;
			ev.preventDefault();
			ev.stopImmediatePropagation();
			target.value = '';
			this.updateRoomList('');
			if (value.startsWith('/')) {
				PS.join('dm-' as RoomID);
				PS.rooms['dm-']?.send(value, target);
				return;
			}

			const arrowIndex = value.indexOf(' \u21d2 ');
			if (arrowIndex >= 0) value = value.slice(arrowIndex + 3);
			if (!/^[a-z0-9-]$/.test(value)) value = toID(value);

			PS.join(value as RoomID);
		}
	};
	updateRoomList(search?: string) {
		if (search && !search.startsWith('/')) search = toID(search);
		const forceNoAutocomplete = this.search === `${search || ''}-`;
		if (search || this.search) {
			if (search === undefined || search === this.search) return;
			this.search = search;
			this.roomListFocusIndex = this.search ? 0 : -1;
		}
		this.roomList = this.getRoomList(forceNoAutocomplete);
		for (const [, rooms] of this.roomList) {
			rooms.sort((a, b) => (b.userCount || 0) - (a.userCount || 0));
		}
	}
	getRoomList(forceNoAutocomplete?: boolean): RoomsSection[] {
		if (this.search.startsWith('/')) {
			return [["Command", [{
				title: "Console", id: 'dm-' as RoomID, desc: `Enter = run command ${this.search}`,
			}]]];
		}
		const searchid = toID(this.search);

		if (!searchid) {
			const roomsCache = PS.mainmenu.roomsCache;
			let spotLightLabel = '';
			const officialRooms = [], chatRooms = [], hiddenRooms = [], spotLightRooms = [];
			for (const room of roomsCache.chat || []) {
				if (room.section !== this.section && this.section !== '') continue;
				if (room.privacy === 'hidden') {
					hiddenRooms.push(room);
				} else if (room.spotlight) {
					spotLightLabel = room.spotlight;
					spotLightRooms.push(room);
				} else if (room.section === 'Official') {
					officialRooms.push(room);
				} else {
					chatRooms.push(room);
				}
			}
			return [
				["Official chat rooms", officialRooms],
				[spotLightLabel, spotLightRooms],
				["Chat rooms", chatRooms],
				["Hidden rooms", hiddenRooms],
			];
		}

		let exactMatch = false;

		const rooms = PS.mainmenu.roomsCache;
		let roomList = [...(rooms.chat || [])];
		for (const room of roomList) {
			if (!room.subRooms) continue;
			for (const title of room.subRooms) {
				roomList.push({
					title,
					desc: `(Subroom of ${room.title})`,
				});
			}
		}

		let results = roomList.filter(room => {
			const titleid = toID(room.title);
			if (titleid === searchid) exactMatch = true;
			return titleid.startsWith(searchid) ||
				toID(room.title.replace(/^The /, '')).startsWith(searchid);
		});
		roomList = roomList.filter(room => !results.includes(room));

		results = results.concat(roomList.filter(room =>
			toID(room.title.toLowerCase().replace(/\b([a-z0-9])[a-z0-9]*\b/g, '$1')).startsWith(searchid) ||
			room.title.replace(/[^A-Z0-9]+/g, '').toLowerCase().startsWith(searchid)
		));

		const hidden: RoomsSection[] = !exactMatch ?
			[["Possible secret room", [{ title: this.search, desc: "(Private room?)" }]]] : [];

		const autoFill = this.lastKeyCode !== 127 && this.lastKeyCode >= 32;
		if (autoFill && !forceNoAutocomplete) {
			results.sort((a, b) => (b.userCount || 0) - (a.userCount || 0));
			const firstTitle = (results[0] || hidden[0][1][0]).title;
			let firstTitleOffset = 0;
			while (
				searchid !== toID(firstTitle.slice(0, firstTitleOffset)) &&
				firstTitleOffset < firstTitle.length // should never happen, but sanity against infinite loop
			) {
				firstTitleOffset++;
			}
			let autoFillValue = firstTitle.slice(firstTitleOffset);
			if (!autoFillValue && toID(firstTitle) !== searchid) {
				autoFillValue = ' \u21d2 ' + firstTitle;
			}
			const oldSearch = this.search;
			const searchElem = this.base!.querySelector<HTMLInputElement>('input[type=search]')!;
			if (autoFillValue) {
				searchElem.value = oldSearch + autoFillValue;
				searchElem.setSelectionRange(oldSearch.length, oldSearch.length + autoFillValue.length);
				this.search += '-';
			}

			return [["Search results", results], ...hidden];
		}

		return [...hidden, ["Search results", results]];
	}
	// BF-HUB-PANEL: the right panel is the Battle Frontier hub —
	// facility buttons while nothing is picked, then the picked
	// format's facility info page. Hub images are optional overlays
	// on gradient placeholders (client/assets/bfhub/README.md).
	bfLastFormat = '';
	bfLastBattle = '';
	bfShowGrid = false;
	bfGenColors: { [gen: number]: [string, string] } = {
		9: ['#d1495b', '#5b2a80'], // BF-HUB-GEN9-COLOR
		8: ['#7d5bb2', '#3f2d66'], 7: ['#e8823a', '#9c4a13'], 6: ['#4d7be0', '#24418c'],
		5: ['#5c6470', '#23272e'], 4: ['#8a9bb0', '#44536b'], 3: ['#3aa16b', '#14633a'],
	};
	bfBackToGrid = (ev: Event) => {
		ev.preventDefault();
		this.bfShowGrid = true;
		this.forceUpdate();
	};
	bfOpenFacility = (ev: Event) => {
		ev.preventDefault();
		const facid = (ev.currentTarget as HTMLElement).getAttribute('data-bffacility');
		const fac = BFFacilities.find(f => f.id === facid);
		if (!fac) return;
		// open the facility's info page and narrow the format menu to its
		// formats — but don't force a pick (no popup)
		PS.bfHubFacility = fac.id;
		this.bfShowGrid = false;
		PS.bfFacilityFilter = fac.formats;
		PS.bfFacilityFilterLabel = fac.name + ' (' + fac.game.replace('Pok\u00e9mon ', '') + ')';
		PS.update();
	};
	bfModeLabel(formatid: string) {
		if (formatid === 'gen3battlepike') return 'Level 50';
		if (formatid === 'gen3battlepikeopenlevel') return 'Open Level';
		const open = formatid.includes('open') ? 'Open Level ' : '';
		if (formatid.endsWith('doubles')) return open + 'Doubles';
		if (formatid.endsWith('triples')) return open + 'Triples';
		if (formatid.endsWith('singles')) return open + 'Singles';
		return '';
	}
	override render() {
		if (this.hidden && PS.isVisiblePanel(this.props.room)) this.hidden = false;
		if (this.hidden) {
			return <PSPanelWrapper room={this.props.room}>{null}</PSPanelWrapper>;
		}
		let fmt = PS.bfHubFormat;
		// while a battle is on the left, show ITS facility's info page —
		// the rules a player wants mid-battle are the ones they're playing
		const bfLeftId = PS.leftPanel ? PS.leftPanel.id : '';
		if (bfLeftId.startsWith('battle-')) {
			if (bfLeftId !== this.bfLastBattle) {
				// a NEW battle resets earlier tile-browsing so its own
				// facility shows (browsing again mid-battle still works)
				this.bfLastBattle = bfLeftId;
				PS.bfHubFacility = '';
			}
			const bfBattleFmt = bfLeftId.split('-')[1];
			if (BFFacilities.some(f => f.formats.includes(bfBattleFmt))) fmt = bfBattleFmt;
		}
		// most recent action wins: a tile-browsed facility beats an older
		// format pick (picking a format clears bfHubFacility, and vice
		// versa a tile click leaves the pick alone but fronts its page)
		let fac = PS.bfHubFacility ? BFFacilities.find(f => f.id === PS.bfHubFacility) : undefined;
		if (!fac && fmt) fac = BFFacilities.find(f => f.formats.includes(fmt));
		const bfKey = fmt + '|' + (fac ? fac.id : '');
		if (bfKey !== this.bfLastFormat) {
			this.bfLastFormat = bfKey;
			this.bfShowGrid = false;
		}
		return <PSPanelWrapper room={this.props.room}><div class="pad" style="max-width:640px">
			{fac && !this.bfShowGrid ? this.bfRenderInfo(fac, fmt) : this.bfRenderGrid(fac)}
		</div></PSPanelWrapper>;
	}
	bfRenderGrid(cur?: BFFacilityInfo) {
		return <div>
			{/* BF-HUB-WELCOME */}
			<h2 style="margin:2px 0 10px">Welcome to Showdown Frontier!</h2>
			<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
				{BFFacilities.map(f => <button
					data-bffacility={f.id} onClick={this.bfOpenFacility}
					style={`display:block;position:relative;height:84px;border-radius:8px;cursor:pointer;padding:0;overflow:hidden;text-align:center;border:1px solid rgba(0,0,0,.4);${cur?.id === f.id ? 'outline:2px solid #3a6ea5;' : ''}background-color:${this.bfGenColors[f.gen][1]};background-image:url(/sprites/bfhub/button-${f.id}.png),linear-gradient(135deg,${this.bfGenColors[f.gen][0]},${this.bfGenColors[f.gen][1]});background-size:cover;background-position:center`}
				>
					<span style="display:inline-block;max-width:95%;padding:3px 10px;border-radius:5px;background:rgba(20,20,25,.62);color:#fff;text-shadow:0 1px 2px #000">
						<strong>{f.name}</strong><br />
						<small>{f.game.replace('Pok\u00e9mon ', '')}</small>
					</span>
				</button>)}
			</div>
		</div>;
	}
	bfRenderInfo(fac: BFFacilityInfo, fmt: string) {
		// one info page per FACILITY — fmt only picks the leaderboard
		const lbFmt = fmt && fac.formats.includes(fmt) ? fmt : fac.formats[0];
		const bfTeams = fac.formats
			.map(f => ({ f, ts: BFSampleTeams[f] || [] }))
			.filter(g => g.ts.length);
		return <div>
			<p style="margin:2px 0 8px">
				<button class="button" onClick={this.bfBackToGrid}>
					<i class="fa fa-chevron-left" aria-hidden></i> All facilities
				</button> {}
				<a class="button" href={`view-bfstreaks-${lbFmt}`} style="float:right">
					<i class="fa fa-trophy" aria-hidden></i> Leaderboard
				</a>
			</p>
			<div style={`position:relative;height:130px;border-radius:8px;overflow:hidden;background-color:${this.bfGenColors[fac.gen][1]};background-image:url(/sprites/bfhub/banner-${fac.id}.png),linear-gradient(120deg,${this.bfGenColors[fac.gen][0]},${this.bfGenColors[fac.gen][1]});background-size:cover;background-position:center`}>
				<div style="position:absolute;left:0;right:0;bottom:0;padding:6px 12px;background:linear-gradient(transparent,rgba(10,10,15,.78) 45%);color:#fff;text-shadow:0 1px 2px #000">
					<strong style="font-size:14pt">{fac.name}</strong> {}
					<small>{fac.game}</small>
				</div>
			</div>
			{/* BF-HUB-OPTIONAL-SECTIONS: empty/missing data = no header */}
			{!!fac.tagline && <p style="margin:8px 0"><em>{fac.tagline}</em></p>}
			{!!fac.rules?.length && <><h3 style="margin:10px 0 4px">Rules</h3>
			<ul style="margin:0;padding-left:22px">
				{fac.rules.map(r => <li><small>{r}</small></li>)}
			</ul></>}
			{!!fac.mechanics?.length && <><h3 style="margin:12px 0 4px">How it works</h3>
			{fac.mechanics.map(m => <p style="margin:6px 0"><small>{m}</small></p>)}</>}
			{!!fac.landmarks?.length && <><h3 style="margin:12px 0 4px">Landmarks</h3>
			<ul style="margin:0;padding-left:22px">
				{fac.landmarks.map(l => <li><small>{l}</small></li>)}
			</ul></>}
			{!!bfTeams.length && <h3 style="margin:12px 0 4px">Sample teams</h3>}
			{bfTeams.map(g => <div>
				{fac.formats.length > 1 && <h4 style="margin:6px 0 2px">{this.bfModeLabel(g.f)}</h4>}
				{g.ts.map(t => <p style="margin:4px 0 10px">
					<strong>{t.name}</strong> <small>by {t.by}</small><br />
					{t.mons.map(m => <PSIcon pokemon={m} />)} {}
					<button class="button" name="bfaddteam" value={`${g.f}|${t.name}|${t.team}`} style="vertical-align:middle">
						<i class="fa fa-clone" aria-hidden></i> Add to teambuilder
					</button>
				</p>)}
			</div>)}
			{!!fac.resources?.length && <h3 style="margin:12px 0 4px">Resources</h3>}{/* BF-HUB-RESOURCES */}
			{!!fac.resources?.length && <p style="margin:4px 0">
				{fac.resources.map(r => <><a class="button" href={r.url} target="_blank" rel="noopener">
					<i class="fa fa-external-link" aria-hidden></i> {r.label}
				</a> </>)}
			</p>}
		</div>;
	}
	bfStockRender() {
		if (this.hidden && PS.isVisiblePanel(this.props.room)) this.hidden = false;
		if (this.hidden) {
			return <PSPanelWrapper room={this.props.room}>{null}</PSPanelWrapper>;
		}
		const rooms = PS.mainmenu.roomsCache;
		this.updateRoomList();

		return <PSPanelWrapper room={this.props.room}><div class="pad">
			<button class="button" style="float:right;font-size:10pt;margin-top:3px" onClick={this.hide}>
				<i class="fa fa-caret-right" aria-hidden></i> Hide
			</button>
			<div class="roomcounters">
				<a class="button" href="users" title="Find an online user">
					<span
						class={`pixelated usercount${Dex.afdMode === true ? ' afd' : ''}`}
						title="Meloetta is PS's mascot! The Aria forme is about using its voice, and represents our chatrooms."
					></span>
					<strong>{rooms.userCount || '-'}</strong> users online
				</a> {}
				<a class="button" href="battles" title="Watch an active battle">
					<span
						class={`pixelated battlecount${Dex.afdMode ? ' afd' : ''}`}
						title="Meloetta is PS's mascot! The Pirouette forme is Fighting-type, and represents our battles."
					></span>
					<strong>{rooms.battleCount || '-'}</strong> active battles
				</a>
			</div>
			{!!PS.leftPanelWidth && Config.includes?.roomlistTopHTML && (
				<div dangerouslySetInnerHTML={{ __html: Config.includes.roomlistTopHTML }} />
			)}
			{/* BF-STRIP-ROOMSEARCH: no chatrooms — streak records live here instead */}
			<div>
				<p><a class="button" href="view-bfstreaks-gen4battletowersingles">Singles streak records</a> {}
				<a class="button" href="view-bfstreaks-gen4battletowerdoubles">Doubles streak records</a> {}
				{/* BF-ROOMS-FACTORY */}
				<a class="button" href="view-bfstreaks-gen4battlefactorysingles">Factory streak records</a> {}
				{/* BF-ROOMS-HALL */}
				<a class="button" href="view-bfstreaks-gen4battlehallsingles">Hall streak records</a> {}
				{/* BF-ROOMS-CASTLE */}
				<a class="button" href="view-bfstreaks-gen4battlecastlesingles">Castle streak records</a> {}
				{/* BF-ROOMS-ARCADE */}
				<a class="button" href="view-bfstreaks-gen4battlearcadesingles">Arcade streak records</a> {}
				{/* BF-ROOMS-TREE */}
				<a class="button" href="view-bfstreaks-gen7battletreesingles">Tree streak records</a> {}
				{/* BF-ROOMS-MAISON */}
				<a class="button" href="view-bfstreaks-gen6battlemaisonsingles">Maison streak records</a> {}
				{/* BF-ROOMS-SUBWAY */}
				<a class="button" href="view-bfstreaks-gen5battlesubwaysingles">Subway streak records</a> {}
				{/* BF-ROOMS-TOWER3 */}
				<a class="button" href="view-bfstreaks-gen3battletowersingles">Emerald Tower streak records</a> {}
				{/* BF-ROOMS-FACTORY3 */}
				<a class="button" href="view-bfstreaks-gen3battlefactorysingles">Emerald Factory streak records</a> {}
				{/* BF-ROOMS-PALACE3 */}
				<a class="button" href="view-bfstreaks-gen3battlepalacesingles">Emerald Palace streak records</a> {}
				{/* BF-ROOMS-ARENA3 */}
				<a class="button" href="view-bfstreaks-gen3battlearenasingles">Emerald Arena streak records</a> {}
				{/* BF-ROOMS-DOME3 */}
				<a class="button" href="view-bfstreaks-gen3battledomesingles">Emerald Dome streak records</a> {}
				{/* BF-ROOMS-PIKE3 */}
				<a class="button" href="view-bfstreaks-gen3battlepike">Emerald Pike streak records</a> {}
				{/* BF-ROOMS-TOWER8 */}
				<a class="button" href="view-bfstreaks-gen8battletowersingles">SwSh Tower streak records</a> {}
				{/* BF-ROOMS-SPARRING8 */}
				<a class="button" href="view-bfstreaks-gen8restrictedsparring">Restricted Sparring records</a></p>
			</div>
			{this.renderRoomList()}
		</div></PSPanelWrapper>;
	}
	getRoomListFocusTitle(): string | undefined {
		return this.roomList.map(([, rooms]) => rooms).reduce((a, b) => a.concat(b))[this.roomListFocusIndex]?.title;
	}
	renderRoomList() {
		const roomsCache = PS.mainmenu.roomsCache;
		if (roomsCache.userCount === undefined) {
			return <div class="roomlist"><h2>Official chat rooms</h2><p><em>Connecting...</em></p></div>;
		}
		if (this.search) {
			// do nothing
		} else if (PS.isOffline) {
			return <div class="roomlist"><h2>Offline</h2></div>;
		} else if (roomsCache.userCount === undefined) {
			return <div class="roomlist"><h2>Official chat rooms</h2><p><em>Connecting...</em></p></div>;
		}

		// Descending order
		let nextOffset = 0;
		return this.roomList.filter(([, rooms]) => rooms.length > 0).map(([title, rooms], sectionCount) => {
			const sortedRooms = rooms.sort((a, b) => (b.userCount || 0) - (a.userCount || 0));
			const offset = nextOffset;
			nextOffset += sortedRooms.length;
			this.roomListLength = nextOffset;

			const index = this.roomListFocusIndex >= offset && this.roomListFocusIndex < nextOffset ?
				this.roomListFocusIndex - offset : -1;

			return <>
				<div class="roomlist">
					<h2>{title}</h2>
					{sortedRooms.map((roomInfo, i) => <div key={roomInfo.title}>
						<a href={`/${roomInfo.id || toID(roomInfo.title)}`} class={`blocklink${i === index ? " cur" : ''}`}>
							{roomInfo.userCount !== undefined && <small style="float:right">({roomInfo.userCount} users)</small>}
							<strong><i class="fa fa-comment-o" aria-hidden></i> {roomInfo.title}<br /></strong>
							<small>{roomInfo.desc || ''}</small>
						</a>
						{roomInfo.subRooms && <div class="subrooms">
							<i class="fa fa-level-up fa-rotate-90" aria-hidden></i> Subrooms: {}
							{roomInfo.subRooms.map(roomName => [<a href={`/${toID(roomName)}`} class="blocklink">
								<i class="fa fa-comment-o" aria-hidden></i> <strong>{roomName}</strong>
							</a>, ' '])}
						</div>}
					</div>)}
				</div>
				{sectionCount === 0 && !!PS.leftPanelWidth && Config.includes?.roomlistAfterFirstHTML && (
					<div dangerouslySetInnerHTML={{ __html: Config.includes.roomlistAfterFirstHTML }} />
				)}
			</>;
		});
	}
}

PS.addRoomType(RoomsPanel);
